import { useEffect, useState } from "react";
import { useNavigate, useParams, Navigate } from "react-router-dom";
import { CheckCircle, ShareNetwork } from "@phosphor-icons/react";
import {
  fetchAccountMe,
  fetchGiveawayById,
  fetchMyParticipation,
  fetchSbpBanks,
  formatStepBodyForDisplay,
  registerGiveawayParticipant,
} from "../api/client";
import {
  cacheGlobalProfileFromAccountMe,
  hasMarketplaceJwtCredentials,
  normalizePhoneDigits,
  readGlobalProfile,
  readTokenForGiveaway,
  saveGlobalProfile,
  saveTokenForGiveaway,
} from "../lib/wicoopProfileStorage";
import { priceView, ru } from "../lib/pricing";
import { readPhoneProofForGiveaway } from "../lib/wicoopPhoneProof";
import { ensureMarketplaceJwtForMaxParticipate } from "../lib/maxWebAppAuth.js";
import { ensureMarketplaceJwtForTelegramParticipate } from "../lib/telegramWebAppAuth.js";
import { shareTelegramGiveawayLink } from "../lib/telegramShareLinks.js";
import { fetchProfileCompletionRedirect } from "../utils/profileCompletion.js";
import { renderTextWithLinks } from "../lib/renderTextWithLinks.jsx";
import styles from "./ProductPage.module.css";

export default function ProductPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [item, setItem] = useState(undefined);
  const [error, setError] = useState(null);
  const [participation, setParticipation] = useState(undefined);
  const [participateBusy, setParticipateBusy] = useState(false);
  const [participateError, setParticipateError] = useState(null);
  const [shareCopied, setShareCopied] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = readTokenForGiveaway(id);
        const g = await fetchGiveawayById(id, token);
        if (!cancelled) setItem(g);
      } catch (e) {
        if (!cancelled) setError(e.message ?? "Ошибка загрузки");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id]);

  useEffect(() => {
    if (!hasMarketplaceJwtCredentials()) return;
    let cancelled = false;
    (async () => {
      try {
        const me = await fetchAccountMe();
        if (!cancelled) cacheGlobalProfileFromAccountMe(me);
      } catch {
        /* ignore */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id]);

  useEffect(() => {
    if (item === undefined || item === null) return;
    let cancelled = false;
    setParticipation(undefined);
    (async () => {
      const token = readTokenForGiveaway(item.id);
      const data = await fetchMyParticipation(item.id, token);
      if (!cancelled) setParticipation(data);
    })();
    return () => {
      cancelled = true;
    };
  }, [item]);

  if (error) {
    return (
      <p className={styles.error} role="alert">
        {error}
      </p>
    );
  }

  if (item === undefined) {
    return <p className={styles.loading}>Загрузка…</p>;
  }

  if (!item) {
    return <Navigate to="/" replace />;
  }

  const participateUrl = `/giveaway/${item.id}/participate`;

  async function handleShare() {
    setShareCopied(false);
    const result = await shareTelegramGiveawayLink({
      giveawayId: item.id,
      shortCode: item.shortCode,
      title: item.title,
    });
    if (result.ok && result.method === "clipboard") {
      setShareCopied(true);
      window.setTimeout(() => setShareCopied(false), 2500);
    }
  }

  const showParticipationBanner =
    participation !== undefined && participation.participates === true;
  const canContinue = participation?.can_continue === true;

  async function handleContinueParticipation() {
    setParticipateError(null);
    try {
      await ensureMarketplaceJwtForMaxParticipate();
      await ensureMarketplaceJwtForTelegramParticipate();
      const completionRedirect = await fetchProfileCompletionRedirect(participateUrl);
      if (completionRedirect) {
        navigate(completionRedirect);
        return;
      }
      navigate(participateUrl);
    } catch (e) {
      setParticipateError(e.message ?? "Не удалось открыть участие");
    }
  }

  async function handleParticipate() {
    setParticipateError(null);
    await ensureMarketplaceJwtForMaxParticipate();
    await ensureMarketplaceJwtForTelegramParticipate();
    const completionRedirect = await fetchProfileCompletionRedirect(participateUrl);
    if (completionRedirect) {
      navigate(completionRedirect);
      return;
    }
    const profile = readGlobalProfile();
    if (!profile) {
      navigate(participateUrl);
      return;
    }
    let jumpEnabled = false;
    try {
      const sbp = await fetchSbpBanks();
      jumpEnabled = !!sbp.jump_enabled;
    } catch {
      jumpEnabled = false;
    }
    const sbpOk =
      !jumpEnabled ||
      (profile.sbp_bank_id != null && String(profile.sbp_bank_id).trim() !== "");
    if (!sbpOk) {
      navigate(participateUrl);
      return;
    }
    const proof = readPhoneProofForGiveaway(item.id, normalizePhoneDigits(profile.phone));
    if (!proof && !hasMarketplaceJwtCredentials()) {
      navigate(participateUrl);
      return;
    }
    setParticipateBusy(true);
    try {
      const reg = {
        phone: profile.phone,
        first_name: profile.first_name,
        last_name: profile.last_name,
        email: profile.email ?? "",
      };
      if (proof) {
        reg.phone_proof = proof;
      }
      if (jumpEnabled && profile.sbp_bank_id != null && String(profile.sbp_bank_id).trim() !== "") {
        reg.sbp_bank_id = parseInt(String(profile.sbp_bank_id).trim(), 10);
      }
      const data = await registerGiveawayParticipant(item.id, reg);
      const token = data.verification_token ?? null;
      if (token) {
        saveTokenForGiveaway(item.id, token);
        saveGlobalProfile(profile);
      }
      navigate(participateUrl);
    } catch (e) {
      setParticipateError(e.message ?? "Не удалось начать участие");
    } finally {
      setParticipateBusy(false);
    }
  }

  return (
    <article className={styles.article}>
      <div className={styles.content}>
        <div className={styles.metaRow}>
          <p className={styles.meta}>
            {item.cabinet && (
              <>
                <span className={styles.cabinetPill}>{item.cabinet.name}</span>
                <span className={styles.dot} aria-hidden />
              </>
            )}
            <span>Мест: {item.spotsLeft}</span>
            {item.cashbackPercent > 0 ? (
              <>
                <span className={styles.dot} aria-hidden />
                <span>Кэшбек {item.cashbackPercent}%</span>
              </>
            ) : null}
          </p>
          <button
            type="button"
            className={`${styles.shareBtn}${shareCopied ? ` ${styles.shareBtnCopied}` : ""}`}
            onClick={handleShare}
            aria-label={shareCopied ? "Ссылка скопирована" : "Поделиться"}
            title={shareCopied ? "Ссылка скопирована" : "Поделиться"}
          >
            {shareCopied ? (
              <CheckCircle className={styles.shareIcon} size={20} weight="regular" />
            ) : (
              <ShareNetwork className={styles.shareIcon} size={20} weight="regular" />
            )}
          </button>
        </div>
        <h1 className={styles.h1}>{item.title}</h1>
        <p className={styles.sub}>
          {priceView(item).finalLabel}
          {priceView(item).refund > 0 ? (
            <span className={styles.subCashback}> · +{ru(priceView(item).refund)} ₽ кэшбек</span>
          ) : null}
        </p>
        {item.description ? (
          <p className={styles.desc}>{item.description}</p>
        ) : null}

        {participation === undefined && (
          <p className={styles.participationLoading}>Проверяем ваше участие…</p>
        )}

        {showParticipationBanner && (
          <div
            className={
              canContinue ? styles.participationCardProgress : styles.participationCard
            }
            role="status"
          >
            <strong className={styles.statusTitle}>
              Вы уже участвуете · {participation.status_title}
            </strong>
            <p className={styles.statusSubtitle}>{participation.status_subtitle}</p>
            {participateError ? (
              <p className={styles.error} role="alert">
                {participateError}
              </p>
            ) : null}
            {canContinue ? (
              <button
                type="button"
                className={styles.ctaInCard}
                onClick={() => void handleContinueParticipation()}
              >
                Продолжить участие
              </button>
            ) : (
              <p className={styles.participationFootnote}>
                Повторная регистрация в этой раздаче недоступна.
              </p>
            )}
          </div>
        )}

        <section className={styles.block}>
          <h2 className={styles.h2}>Условия участия</h2>
          <ul className={styles.list}>
            {item.conditions.map((c) => (
              <li key={c}>{c}</li>
            ))}
          </ul>
        </section>

        <section className={styles.block}>
          <h2 className={styles.h2}>Этапы</h2>
          <ol className={styles.steps}>
            {item.steps.map((s, i) => (
              <li key={`${i}-${s.title}`} className={styles.stepRow}>
                <span className={styles.stepNum}>{i + 1}</span>
                <div>
                  <strong className={styles.stepTitle}>
                    {s.title}
                    {s.requires_screenshot ? (
                      <span className={styles.stepShotBadge}>нужен скриншот</span>
                    ) : null}
                  </strong>
                  <p className={styles.stepBody}>
                    {renderTextWithLinks(formatStepBodyForDisplay(s.body, i, item))}
                  </p>
                  {(s.illustration_url || "").trim() ? (
                    <div className={styles.stepIllustration}>
                      <img
                        src={String(s.illustration_url).trim()}
                        alt=""
                        loading="lazy"
                        className={styles.stepIllustrationImg}
                      />
                    </div>
                  ) : null}
                </div>
              </li>
            ))}
          </ol>
        </section>

        {participation === undefined ? (
          <div className={styles.ctaPlaceholder} aria-hidden />
        ) : showParticipationBanner ? null : (
          <>
            {participateError ? (
              <p className={styles.error} role="alert">
                {participateError}
              </p>
            ) : null}
            <div className={styles.ctaDock} role="presentation">
              <button
                type="button"
                className={styles.cta}
                disabled={participateBusy}
                onClick={handleParticipate}
              >
                {participateBusy ? "Подключаем…" : "Участвовать"}
              </button>
            </div>
          </>
        )}
      </div>
    </article>
  );
}
